import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useBusinessGame } from './useBusinessGame';
import { Building2, Users, Smile, Trophy, Timer, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const BusinessGame: React.FC = () => {
  const navigate = useNavigate();
  const { state, startGame, makeDecision, endGame } = useBusinessGame();

  const content = !state.gameStarted ? (
    <div className="text-center">
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-center gap-4">
          <Building2 className="w-6 h-6 text-primary" />
          <span>Crea y administra tu propio negocio</span>
        </div>
        <div className="flex items-center justify-center gap-4">
          <Users className="w-6 h-6 text-green-500" />
          <span>Toma decisiones estratégicas</span>
        </div>
        <div className="flex items-center justify-center gap-4">
          <Smile className="w-6 h-6 text-yellow-500" />
          <span>Aprende sobre emprendimiento</span>
        </div>
      </div>
      <Button onClick={startGame} size="lg">¡Comenzar!</Button>
    </div>
  ) : (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            <div>
              <p className="text-sm text-gray-500">Dinero</p>
              <p className="text-lg font-bold">${state.money}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-sm text-gray-500">Clientes</p>
              <p className="text-lg font-bold">{state.customers}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Smile className="w-6 h-6 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-500">Satisfacción</p>
              <p className="text-lg font-bold">{state.satisfaction}%</p>
            </div>
          </div>
        </Card>
      </div>

      {state.currentDecisionIndex < state.decisions.length ? (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            {state.decisions[state.currentDecisionIndex].question}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {state.decisions[state.currentDecisionIndex].options.map((option, index) => (
              <Button
                key={index}
                onClick={() => makeDecision(index)}
                variant="outline"
                className="h-auto py-4 text-left"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">{option.text}</span>
                  <span className="text-sm text-gray-500">{option.description}</span>
                </div>
              </Button>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-center">
          <h3 className="text-xl font-bold mb-4">¡Juego Completado!</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-primary/10 rounded-lg">
              <p className="font-semibold">Dinero Final</p>
              <p className="text-2xl font-bold">${state.money}</p>
            </div>
            <div className="p-4 bg-green-100 rounded-lg">
              <p className="font-semibold">Clientes Totales</p>
              <p className="text-2xl font-bold">{state.customers}</p>
            </div>
            <div className="p-4 bg-yellow-100 rounded-lg">
              <p className="font-semibold">Satisfacción Final</p>
              <p className="text-2xl font-bold">{state.satisfaction}%</p>
            </div>
          </div>
          <Button onClick={endGame} size="lg">
            Finalizar y Guardar Progreso
          </Button>
        </Card>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-4xl mx-auto p-2 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        {/* Botón Volver */}
        <button
          onClick={() => navigate('/games')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver a Juegos</span>
        </button>

        {/* Header del juego */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6 gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary animate-pulse" />
            <h2 className="text-xl sm:text-2xl font-bold">Mi Primer Negocio</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <span className="text-base sm:text-lg font-semibold">Puntos: {state.score}</span>
            </div>
            <div className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-500" />
              <span className="text-base sm:text-lg font-semibold">
                Tiempo: {Math.floor(state.timeLeft / 60)}:{(state.timeLeft % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* Contenido del juego */}
        {content}
      </div>
    </div>
  );
};
